import React, { memo, useCallback } from 'react'
import moment from 'moment'

import { useAppDispatch } from 'hooks/reduxHooks'
import { accountAccessShowLogin } from 'actions/UiActions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useBedbankSelectDates from 'hooks/OfferPage/useBedbankSelectDates'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'components/Luxkit/Button/TextButton'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BedbankSelectDates from './BedbankSelectDates'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LineTagAltIcon from 'components/Luxkit/Icons/line/LineTagAltIcon'
import Group from 'components/utils/Group'
import LineExclamationTriangleIcon from 'components/Luxkit/Icons/line/LineExclamationTriangleIcon'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'

interface Props {
  checkinDate?: moment.Moment;
  checkoutDate?: moment.Moment;
  offer: App.BedbankOffer;
  rates: Array<App.BedbankRate>;
  fetchingRates?: boolean;
  packageView: App.BedbankOfferPackageView;
  onShowMoreDetails: () => void;
}

function BedbankBookingPane({
  checkinDate,
  checkoutDate,
  offer,
  rates,
  packageView,
  fetchingRates,
  onShowMoreDetails,
}: Props) {
  const validCheckinCheckout = !!(checkinDate && checkoutDate)
  const offerPageDispatch = useOfferPageDispatch()
  const authDispatch = useAppDispatch()
  const discountPercent = Math.round(rates[0]?.discount * 100)
  const onCalendarOpenSelectDates = useBedbankSelectDates()

  const hotelAvailable = packageView.available
  const canPurchase = rates.length > 0 && packageView.capacityValid && hotelAvailable && !fetchingRates

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch])

  return <div>
    <WalledContent
      enforced={offer.walledGarden}
      wall={(
        <Group direction="vertical" horizontalAlign="end" gap={12}>
          <BodyTextBlock variant="medium" startIcon={<LineTagAltIcon />}>
            {!discountPercent ? 'Members get access to exclusive rates' : `Get ${discountPercent}% off with member prices`}
          </BodyTextBlock>
          <Group direction="horizontal" gap={8} horizontalAlign="end">
            <TextButton kind="secondary" onClick={() => authDispatch(accountAccessShowLogin())}>
              Log In
            </TextButton>
            <TextButton kind="primary" onClick={() => authDispatch(accountAccessShowLogin())}>
              Sign up for free
            </TextButton>
          </Group>
        </Group>
      )}
    >
      <Group
       direction="vertical"
       tabletHorizontalAlign="end"
       gap={8}
      >
        {validCheckinCheckout && !hotelAvailable && packageView.capacityValid &&
          <BodyTextBlock variant="medium" startIcon={<LineInfoCircleIcon />}>
            This {offer.saleUnit} is sold out on your dates
          </BodyTextBlock>
        }
        {hotelAvailable && !packageView.capacityValid &&
          <BodyTextBlock variant="medium" startIcon={<LineExclamationTriangleIcon />}>
            Max number of guests exceeded
          </BodyTextBlock>
        }
        <Group
          direction="vertical"
          tabletDirection="horizontal"
          tabletHorizontalAlign="end"
          gap={8}
        >
          <TextButton
            kind="tertiary"
            onClick={onShowMoreDetails}
          >
            View amenities & policies
          </TextButton>
          {hotelAvailable && packageView.capacityValid && !validCheckinCheckout && <BedbankSelectDates />}
          {!canPurchase && (<>
            {validCheckinCheckout && packageView.capacityValid && (
              <TextButton
                kind="primary"
                onClick={onCalendarOpenSelectDates}
                data-testid="select-your-dates-button"
              >
                Change your dates
              </TextButton>
            )}
            {hotelAvailable && !packageView.capacityValid && (
              <TextButton
                kind="tertiary"
                onClick={onToggleFilter}
                data-filter="capacity"
              >
                Add another {offer.saleUnit}
              </TextButton>
            )}
          </>)}
        </Group>
      </Group>
    </WalledContent>
    <LoadingIndicator floating visible={fetchingRates} />
  </div>
}

export default memo(BedbankBookingPane)
